import React, { useState, useEffect } from 'react';
import './AccountSettings.css';

function AccountSettings({ token, api, user, onLogout }) {
  const [displayName, setDisplayName] = useState('');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [deletePw, setDeletePw] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState(null); // { text, type: 'success' | 'error' }

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName || '');
    }
  }, [user]);

  const headers = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  });

  const flash = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleUpdateName = async () => {
    if (!displayName.trim()) return;
    setBusy('name');
    try {
      const res = await fetch(`${api}/auth/profile`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({ display_name: displayName.trim() }),
      });
      const data = await res.json();
      if (res.ok) flash('Display name updated');
      else flash(data.detail || 'Update failed', 'error');
    } catch { flash('Network error', 'error'); }
    setBusy('');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { flash('Passwords do not match', 'error'); return; }
    if (newPw.length < 6) { flash('Password must be at least 6 characters', 'error'); return; }
    setBusy('pw');
    try {
      const res = await fetch(`${api}/auth/change-password`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
      });
      const data = await res.json();
      if (res.ok) {
        flash('Password changed successfully');
        setCurrentPw(''); setNewPw(''); setConfirmPw('');
      } else flash(data.detail || 'Password change failed', 'error');
    } catch { flash('Network error', 'error'); }
    setBusy('');
  };

  const handleDeleteAccount = async () => {
    if (!deletePw) { flash('Enter your password to confirm', 'error'); return; }
    setBusy('delete');
    try {
      const res = await fetch(`${api}/auth/account`, {
        method: 'DELETE',
        headers: headers(),
        body: JSON.stringify({ password: deletePw }),
      });
      const data = await res.json();
      if (res.ok) {
        onLogout();
      } else flash(data.detail || 'Deletion failed', 'error');
    } catch { flash('Network error', 'error'); }
    setBusy('');
  };

  return (
    <div className="acct-root">
      {message && (
        <div className={`acct-flash ${message.type}`}>{message.text}</div>
      )}

      {/* Profile Section */}
      <div className="acct-section">
        <div className="acct-section-title">Profile</div>
        <div className="acct-divider" />
        <div className="acct-field">
          <label className="acct-label">Email</label>
          <div className="acct-static">{user?.email || '—'}</div>
        </div>
        <div className="acct-field">
          <label className="acct-label">Display Name</label>
          <div className="acct-row">
            <input
              className="acct-input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
            />
            <button
              className="acct-btn-sm"
              onClick={handleUpdateName}
              disabled={busy === 'name'}
            >
              {busy === 'name' ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Change Password Section */}
      <div className="acct-section">
        <div className="acct-section-title">Change Password</div>
        <div className="acct-divider" />
        <form onSubmit={handleChangePassword} className="acct-pw-form">
          <input
            className="acct-input"
            type="password"
            placeholder="Current password"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            required
          />
          <input
            className="acct-input"
            type="password"
            placeholder="New password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            required
            minLength={6}
          />
          <input
            className="acct-input"
            type="password"
            placeholder="Confirm new password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            required
          />
          <button
            className="acct-btn"
            type="submit"
            disabled={busy === 'pw' || !currentPw || !newPw || !confirmPw}
          >
            {busy === 'pw' ? 'Changing…' : 'Change Password'}
          </button>
        </form>
      </div>

      {/* Danger Zone */}
      <div className="acct-section acct-danger">
        <div className="acct-section-title" style={{ color: '#e07a5f' }}>Danger Zone</div>
        <div className="acct-divider" style={{ borderColor: 'rgba(224,122,95,0.2)' }} />
        {!showDeleteConfirm ? (
          <button
            className="acct-btn-danger"
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete Account
          </button>
        ) : (
          <div className="acct-delete-confirm">
            <p className="acct-delete-warn">
              This will permanently delete your account and all data (memories, conversations, settings). This cannot be undone.
            </p>
            <input
              className="acct-input"
              type="password"
              placeholder="Enter your password to confirm"
              value={deletePw}
              onChange={(e) => setDeletePw(e.target.value)}
            />
            <div className="acct-delete-actions">
              <button
                className="acct-btn-cancel"
                onClick={() => { setShowDeleteConfirm(false); setDeletePw(''); }}
              >
                Cancel
              </button>
              <button
                className="acct-btn-danger-confirm"
                onClick={handleDeleteAccount}
                disabled={busy === 'delete' || !deletePw}
              >
                {busy === 'delete' ? 'Deleting…' : 'Permanently Delete'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AccountSettings;
